
import {empty as observableEmpty, of as observableOf } from 'rxjs';

import { catchError, switchMap, withLatestFrom} from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Actions, createEffect, Effect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AuthService } from '@shared/services/auth/auth.service';
import { InvitedeskApiService } from '@shared/services/api/legacy/invitedesk-api.service';
import { SuccessResponse } from '@shared/services/api/legacy/response';
import { IUserProfile } from '@shared/services/api/legacy/users-service/user-model';
import { AuthActionTypes, LoggedIn } from '@state/actions/auth/auth.actions';
import { IState } from '@state/state.module';
import { AppActionTypes, CrmsRetrieved, ProfileRetrieved, RetrieveCrmsPending, RetrievePrincipalProfilePending,
    RetrieveProvisioningPending,
    RetrieveTenantSettingsPending, TenantSettingsRetrieved,
    ProvisioningRetrieved} from '../../actions/app/app.actions';
import { Router } from '@angular/router';
import { LocaleService } from '@shared/services/locale/locale.service';
import { DashboardApiService } from '@shared/services/api/dashboard/dashboard-api.service';
import { IConnectorValidations } from '@shared/services/api/dashboard/integrations-service/integrations-model';
import { IPermission } from '@shared/services/api/legacy/settings-service/setting-model';
import { IGetUserProvisioning, IUserProvisioning } from '@shared/services/api/dashboard/user-provisionings-service/user-provsionings-model';

@Injectable()
export class AppEffects {
    constructor(
        private actions$: Actions,
        private store$: Store<IState>,
        private inviteDeskApi: InvitedeskApiService,
        private dashboardApi: DashboardApiService,
        private authService: AuthService,
        private router: Router,
        private localeService: LocaleService
    ) { }

    public initToken$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActionTypes.INIT_TOKEN),
            withLatestFrom(this.store$),
            switchMap(([x, store]: [LoggedIn, IState]) => {
                if (!store.auth.principal.token.isExpired) {
                    return observableOf(new RetrievePrincipalProfilePending());
                } else {
                    this.authService.clearToken();
                    return observableEmpty();
                }
            })));


    public loggedIn$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActionTypes.LOGGED_IN),
            withLatestFrom(this.store$),
            switchMap(([x, store]: [LoggedIn, IState]) => observableOf(new RetrievePrincipalProfilePending())),));

    public loggedIn2$ = createEffect(() =>
            this.actions$.pipe(
                ofType(AuthActionTypes.INIT_TOKEN, AuthActionTypes.LOGGED_IN),
                withLatestFrom(this.store$),
                switchMap(([x, store]: [LoggedIn, IState]) => observableOf(new RetrieveCrmsPending())),));

    public loggedIn3$ = createEffect(() =>
            this.actions$.pipe(
                ofType(AuthActionTypes.INIT_TOKEN, AuthActionTypes.LOGGED_IN),
                withLatestFrom(this.store$),
                switchMap(([x, store]: [LoggedIn, IState]) => observableOf(new RetrieveTenantSettingsPending())),));

    public loggedIn4$ = createEffect(() =>
            this.actions$.pipe(
                ofType(AuthActionTypes.INIT_TOKEN, AuthActionTypes.LOGGED_IN),
                withLatestFrom(this.store$),
                switchMap(([x, store]: [LoggedIn, IState]) => observableOf(new RetrieveProvisioningPending())),));

    @Effect()
    public RetrievePrincipalProfilePending$ =
        this.actions$.pipe(
            ofType(AppActionTypes.RETRIEVE_PROFILE_PENDING),
            switchMap((pending: RetrievePrincipalProfilePending) => {
                return this.inviteDeskApi.users
                    .getPrincipalUserProfile().pipe(
                    switchMap((profile: SuccessResponse<IUserProfile>) =>
                        observableOf(new ProfileRetrieved(
                            {
                                id: profile.data.id,
                                name: profile.data.lastName,
                                firstName: profile.data.firstName,
                                languageId: this.localeService.getLanguage(profile.data.languageId),
                                pictureUrl: profile.data.pictureUrl,
                                jobTitle: profile.data.jobTitle,
                                aadRemoteId : profile.data.aadRemoteId,
                                tenants: profile.data.tenants,
                                isSupport : profile.data.isSupport,
                                localeId: this.localeService.getLocale(profile.data.languageId)
                            }
                        ))),
                    catchError(error => observableOf({ error })));
            })
        );


        @Effect()
        public retrieveCrmsPending$ =
            this.actions$.pipe(
                ofType(AppActionTypes.RETRIEVE_CRMS_PENDING),
                switchMap((pending: RetrieveCrmsPending) => {
                    return this.dashboardApi.integrations
                        .getConnectorValidation().pipe(
                        switchMap((profile: SuccessResponse<IConnectorValidations>) =>
                            observableOf(new CrmsRetrieved({ crms: profile.data.crms }))),
                        catchError(error => observableOf({ error })));
                })
            );

        @Effect()
        public retrieveTenantSettingsPending$ =
            this.actions$.pipe(
                ofType(AppActionTypes.RETRIEVE_TENANTSETTINGS_PENDING),
                switchMap((pending: RetrieveTenantSettingsPending) => {
                    return this.inviteDeskApi.settings
                        .getPermission().pipe(
                        switchMap((data: SuccessResponse<IPermission>) =>
                            observableOf(new TenantSettingsRetrieved({ setttings: data?.data }))),
                        catchError(error => observableOf({ error })));
                })
            );

        @Effect()
        public retrieveProvisioningPending$ =
            this.actions$.pipe(
                ofType(AppActionTypes.RETRIEVE_PROVISIONING_PENDING),
                switchMap((pending: RetrieveProvisioningPending) => {
                    return this.dashboardApi.userProvisionings
                        .getProvisioning().pipe(
                        switchMap((profile: SuccessResponse<IGetUserProvisioning>) =>
                            observableOf(new ProvisioningRetrieved({ provisioning: profile.data }))),
                        catchError(error => observableOf({ error })));
                })
            );

}
